import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { Safeguard } from "@scenes/safeguard";
import { useModal } from "@root/client/modal";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Resource } from "../resource";
import { ContentLoader } from "./contentLoader";
import { Props } from "./types";
import styles from "./styles.scss";

function ResourcesList({
  projectResources,
  isRequestPending,
  canLoadMore,
  retrieveNextResources,
  project,
  columns,
}: Props) {
  const { t, i18n } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "resource",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "resource",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { pushView } = useModal();
  const currentLang =
    ["fr", "en"].find((lang) => new RegExp(lang, "i").test(i18n.language)) ||
    "fr";

  return (
    <div className={styles.resourcesList}>
      <div className={styles.heading}>
        <p className={styles.label}>{t("membersList.name")}</p>
        {columns.map(({ key }) => {
          const criteria = template.find(({ uuid }) => uuid === key);
          const label = criteria
            ? criteria.labels[currentLang as keyof typeof criteria.labels] ||
              criteria.name
            : t(`projectMembersListingConfiguration.columnNames.${key}`, key);

          return (
            <p key={key} className={styles.label}>
              {label}
            </p>
          );
        })}
        <Safeguard useCases={[UseCases.editProjectMembersListingConfiguration]}>
          {({ canEditProjectMembersListingConfiguration }) => (
            <div className={`${styles.label} ${styles.cogWheel}`}>
              {canEditProjectMembersListingConfiguration && (
                <div
                  onClick={() =>
                    pushView({
                      key: UseCases.retrieveProjectMembersListingConfiguration,
                      data: { project },
                    })
                  }
                >
                  <CogWheelIcon className={styles.cogIcon} />
                </div>
              )}
            </div>
          )}
        </Safeguard>
      </div>
      {isRequestPending && projectResources.length === 0 ? (
        <ContentLoader />
      ) : (
        <div className={styles.listScroll}>
          <InfiniteScroll id="projectResources">
            {() => (
              <React.Fragment>
                <ul className={styles.list}>
                  {projectResources.map((projectResource) => (
                    <li key={projectResource.uuid} className={styles.item}>
                      <Resource
                        projectResource={projectResource}
                        columns={columns}
                      />
                    </li>
                  ))}
                </ul>
                <Flag
                  name="projectResourcesFlag"
                  retrieveItems={() =>
                    !isRequestPending && retrieveNextResources()
                  }
                  isDisabled={isRequestPending || !canLoadMore}
                />
              </React.Fragment>
            )}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}

export { ResourcesList };
